import { useCallback, useEffect, useState } from 'react';
import AgentInstructionsList from './AgentInstructionsList';

import { useAgentApi } from '../Services/AgentApiProvider';
import {
  AssistantInstructionApi,
  AssistantInstructionListApi,
} from '../Services/AgentApiProviderTypes';
import useLatestRequest from '../Hooks/useLatestRequest';
import useTablePagination from '../Hooks/useTablePagination';

function AgentInstructionsTab({ agentId, canEdit }: { agentId: string; canEdit: boolean }) {
  const { getAgentInstructions, getRelevantInstruction } = useAgentApi();

  const [agentInstructions, setAgentInstructions] = useState<AssistantInstructionApi[]>([]);
  const [totalInstructions, setTotalInstructions] = useState<number>(0);
  const [instructionsLoading, setInstructionsLoading] = useState<boolean>(false);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>('');
  const [onUpdate, setOnUpdate] = useState<boolean>(false);

  const tableTitle = 'Agent Instructions';
  const { initialPageSize, resetToggle, storedSkip, storedLimit, firstPage } =
    useTablePagination(tableTitle);
  const beginRequest = useLatestRequest();

  const updateTrigger = (): void => setOnUpdate((prev) => !prev);

  const fetchInstructions = useCallback(
    async (skip: number, limit: number, searchTerm = '') => {
      const isLatest = beginRequest();
      setInstructionsLoading(true);
      try {
        let responseData: AssistantInstructionListApi;
        if (searchTerm) {
          responseData = await getRelevantInstruction(agentId, searchTerm, limit);
        } else {
          responseData = await getAgentInstructions(agentId, skip, limit);
        }
        if (!isLatest()) return;
        setAgentInstructions(responseData.items || []);
        setTotalInstructions(responseData.totalCount || 0);
      } catch (err) {
        if (!isLatest()) return;
        if (err.response && err.response.status === 404) {
          setAgentInstructions([]);
          setTotalInstructions(0);
        }
      } finally {
        if (isLatest()) setInstructionsLoading(false);
      }
    },
    [agentId, getAgentInstructions, getRelevantInstruction, beginRequest],
  );

  const handleSearch = useCallback(
    (searchTerm: string): void => {
      setCurrentSearchTerm(searchTerm);
      const { skip, limit } = firstPage();
      fetchInstructions(skip, limit, searchTerm);
    },
    [fetchInstructions, firstPage],
  );

  const handleTableChange = (skip: number, limit: number): void => {
    fetchInstructions(skip, limit, currentSearchTerm);
  };

  useEffect(() => {
    fetchInstructions(storedSkip(), storedLimit(), currentSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onUpdate]);

  return (
    <div className="radius-yes p-2 bg-yes col-6">
      <div className="gap d-flex flex-row-to-col flex-column">
        <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
          <AgentInstructionsList
            agentId={agentId}
            canEdit={canEdit}
            agentInstructions={agentInstructions}
            onUpdateTrigger={updateTrigger}
            onSearch={handleSearch}
            loading={instructionsLoading}
            totalInstructions={totalInstructions}
            persistedPageSize={initialPageSize}
            resetPaginationToggle={resetToggle}
            tableTitle={tableTitle}
            onChangePage={handleTableChange}
          />
        </div>
      </div>
    </div>
  );
}

export default AgentInstructionsTab;
